interface Props {
  name: string;
  subject: string;
  preheader: string;
  saving: boolean;
  savedId: string | null;
  error: string | null;
  onChange: (patch: Partial<{ name: string; subject: string; preheader: string }>) => void;
  onOpenTemplates: () => void;
  onSave: () => void;
  onLogout: () => void;
  onDismissToast: () => void;
}

const FIELD_HELP = {
  name: "Etiqueta interna que verás en SendGrid para identificar este template. No la ven los destinatarios. Usa snake_case sin acentos, ej: welcome_riders_abril.",
  subject: "Es el asunto del correo: lo primero que ven los destinatarios en su bandeja. Mantenlo corto (máx. 50 caracteres) y claro.",
  preheader:
    "Texto de preview que aparece en gris al lado del asunto en la inbox. Complementa el subject sin repetirlo. Ideal: 60–90 caracteres.",
} as const;

export function MailingMeta({
  name,
  subject,
  preheader,
  saving,
  savedId,
  error,
  onChange,
  onOpenTemplates,
  onSave,
  onLogout,
  onDismissToast,
}: Props) {
  const canSave = name.trim() !== "" && subject.trim() !== "" && preheader.trim() !== "" && !saving;

  return (
    <header className="flex items-center gap-4 border-b border-slate-200 bg-white px-6 py-3 shadow-soft">
      <div className="flex items-center gap-3">
        <div className="flex h-8 items-center rounded-md bg-tvs-blue px-2">
          <img src="/white-logo.png" alt="TVS Motor" className="h-5 w-auto" />
        </div>
        <span className="text-sm font-semibold text-tvs-blue">Mailings</span>
      </div>

      <div className="ml-4 flex flex-1 items-end gap-3">
        <Field
          label="Nombre interno"
          help={FIELD_HELP.name}
          value={name}
          onChange={(v) => onChange({ name: v })}
          placeholder="ej. welcome_riders_abril"
        />
        <Field
          label="Subject"
          help={FIELD_HELP.subject}
          value={subject}
          onChange={(v) => onChange({ subject: v })}
          placeholder="Asunto del correo"
        />
        <Field
          label="Preheader"
          help={FIELD_HELP.preheader}
          value={preheader}
          onChange={(v) => onChange({ preheader: v })}
          placeholder="Texto preview en la inbox"
        />
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onOpenTemplates}
          title="Vuelve a la plantilla oficial vacía. Vas a perder los cambios actuales."
          className="btn-secondary text-sm"
        >
          Reiniciar
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={!canSave}
          title={canSave ? "Guarda el mailing como template en SendGrid (no envía correos)" : "Completa nombre, subject y preheader antes de guardar"}
          className="btn-primary text-sm"
        >
          {saving ? "Guardando…" : "Guardar en SendGrid"}
        </button>
        <button
          type="button"
          onClick={onLogout}
          title="Cerrar sesión"
          className="ml-2 text-xs text-slate-500 hover:text-tvs-blue"
        >
          Salir
        </button>
      </div>

      {(savedId || error) && (
        <div
          role="status"
          aria-live="polite"
          className={`tvs-toast fixed right-6 top-20 flex max-w-md items-start gap-3 rounded-lg border bg-white p-3 pr-8 shadow-lg ${
            error ? "border-red-200" : "border-emerald-200"
          }`}
        >
          <span
            className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white ${
              error ? "bg-tvs-red" : "bg-emerald-500"
            }`}
            aria-hidden="true"
          >
            {error ? "!" : "✓"}
          </span>
          <div className="min-w-0 flex-1">
            {savedId && (
              <p className="text-sm text-slate-700">
                Template guardado en SendGrid:{" "}
                <code className="break-all text-tvs-blue">{savedId}</code>
              </p>
            )}
            {error && <p className="break-words text-sm text-tvs-red">{error}</p>}
          </div>
          <button
            type="button"
            onClick={onDismissToast}
            aria-label="Cerrar"
            className="absolute right-2 top-2 text-slate-400 transition-colors hover:text-slate-700"
          >
            ✕
          </button>
        </div>
      )}
    </header>
  );
}

function Field({
  label,
  help,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  help: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="flex flex-1 flex-col">
      <span className="field-label flex items-center gap-1.5">
        {label}
        <HelpIcon text={help} />
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="input-field"
      />
    </label>
  );
}

function HelpIcon({ text }: { text: string }) {
  return (
    <span className="tvs-help group relative inline-flex">
      <span
        aria-label={text}
        tabIndex={0}
        className="flex h-3.5 w-3.5 cursor-help items-center justify-center rounded-full border border-slate-300 text-[9px] font-bold text-slate-500 transition-colors hover:border-tvs-blue hover:text-tvs-blue focus:outline-none focus:ring-2 focus:ring-tvs-blue focus:ring-offset-1"
      >
        ?
      </span>
      <span
        role="tooltip"
        className="pointer-events-none absolute left-0 top-5 z-50 w-64 rounded-md border border-slate-200 bg-slate-900 p-2.5 text-[11px] font-normal normal-case leading-snug tracking-normal text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
      >
        {text}
      </span>
    </span>
  );
}
